import React, { useEffect, useMemo, useRef, useState } from "react";
import { ImagePost, Post } from "../../../types";
import "./style.css";
import ReactPlayer from "react-player";
import ChatAudioMessage from '../../chat-audio-message';
import { BUCKET_PACOCA_URL } from "../../../global-components/src/utils/vars";
import { useTheme } from "../../../global-components/src";
import { useVideo } from "../../../contexts/VideoContext";
import ImageModal from "./image-modal";
import { getPostVideoAspectRatio } from "./video-layout";

interface ImgPostProps {
    images: ImagePost[];
    img_account?: string;
    post?: Post;
    onLike?: () => void;
    onComment?: () => void;
    onShare?: () => void;
    setPost?: React.Dispatch<React.SetStateAction<Post | null>>;
    setPosts?: React.Dispatch<React.SetStateAction<Post[]>>;
    mock?: boolean;
    is_repost?: boolean;
}

const ImgPost: React.FC<ImgPostProps> = ({ images, is_repost = false, post, onComment, onShare, setPost, setPosts, img_account, mock }) => {
    const imgRef = useRef<HTMLImageElement | null>(null);
    const videoRef = useRef<ReactPlayer | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [openImage, setOpenImage] = useState<number | null>(null);
    const { isDarkMode } = useTheme();
    const firstRun = useRef(true);
    const [isAtTop, setIsAtTop] = useState(false);
    const statusbarStyleClose = useMemo(() => (window.location.pathname === "/" || (window.location.pathname.includes("@") && !window.location.pathname.includes("chat"))) && isAtTop ? (isDarkMode ? "#181818" : "#F4F2EE") : (isDarkMode ? "#000" : "#fff"), [isAtTop, window.location.pathname, isDarkMode]);
    const [orientation, setOrientation] = useState<"square" | "horizontal">("square");
    const [videoAspectRatio, setVideoAspectRatio] = useState(1);
    const { mutedVideo, setMutedVideo } = useVideo();

    const getImagePath = (path: string) => {
        if (path.includes("http://") || path.includes("https://") || path.includes("blob"))
            return path;
        return BUCKET_PACOCA_URL + "/" + path;
    };

    const audioAvatarPath = img_account
        ? (/^(https?:|blob:|data:|\/)/i.test(img_account) ? img_account : getImagePath(img_account))
        : undefined;
    const audioAvatarName = post?.user?.name || post?.user?.user_name;

    // impede de voltar tela ao clicar em voltar com modal aberto
    useEffect(() => {
        const handlePopState = () => {
            if (openImage !== null) setOpenImage(null);
        };
        if (openImage !== null) {
            window.history.pushState({ modalOpen: true }, "");
            window.addEventListener('popstate', handlePopState);
        }
        return () => window.removeEventListener('popstate', handlePopState);
    }, [openImage]);

    const closeImageModal = () => {
        if (openImage !== null) {
            setOpenImage(null);
            if (window.history.state?.modalOpen) window.history.back();
        }
    };

    useEffect(() => {
        if (firstRun.current) { firstRun.current = false; return; }
        if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({ statusBarStyle: openImage != null ? "#000" : statusbarStyleClose })
            );
        }
    }, [openImage]);

    const verificarOrientacaoImagem = (urlDaImagem: string) => {
        const img = new Image();
        img.onload = () => {
            if (imgRef.current && img.width < img.height) {
                imgRef.current.style.aspectRatio = "1 / 1";
            }
        };
        img.src = urlDaImagem;
    };

    const checkVideoVisibility = () => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const isVisible = rect.top < windowHeight && rect.bottom > 0;
        if (!isVisible) { setIsPlaying(false); return; }
        if (rect.top > 100 && rect.top < windowHeight / 2) setIsPlaying(true);
        else setIsPlaying(false);
    };

    useEffect(() => {
        let rafId: number;
        const handleScroll = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(checkVideoVisibility);
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        checkVideoVisibility();
        return () => {
            window.removeEventListener("scroll", handleScroll);
            cancelAnimationFrame(rafId);
        };
    }, []);

    const verificarOrientacaoVideo = () => {
        if (videoRef.current) {
            const player = videoRef.current.getInternalPlayer() as HTMLVideoElement;
            if (player) {
                const largura = player.videoWidth;
                const altura = player.videoHeight;
                setVideoAspectRatio(getPostVideoAspectRatio(largura, altura));
            }
        }
    };

    useEffect(() => {
        if (images?.length > 0 && images[0]?.type === 0) {
            verificarOrientacaoImagem(getImagePath(images[0]?.path));
        }
    }, [images]);

    useEffect(() => {
        setVideoAspectRatio(1);
    }, [images?.[0]?.path]);

    useEffect(() => {
        if (openImage != null && openImage >= 0) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "auto";
    }, [window.location.href, openImage]);

    useEffect(() => {
        const player = videoRef.current?.getInternalPlayer() as HTMLVideoElement;
        if (!player) return;
        const handleVolumeChange = () => setMutedVideo(player.muted);
        player.addEventListener("volumechange", handleVolumeChange);
        return () => player.removeEventListener("volumechange", handleVolumeChange);
    }, []);

    if (!images || images?.length === 0) return null;

    return (
        <>
            {images?.length === 1 ? (
                images[0]?.type === 0 ? (
                    <div className={`row user-select-none row-img-post ${orientation}`}>
                        <div className="col col-img-post">
                            <span onClick={() => openImage === 0 ? setOpenImage(null) : setOpenImage(0)} style={{ width: "100%" }}>
                                <img
                                    style={{ marginLeft: 0, width: "100%", height: "100%" }}
                                    alt="Imagem da publicação"
                                    onError={(e) => (e.currentTarget.src = "/img/image_not_found.png?v=1")}
                                    src={getImagePath(images[0].path)}
                                    className="img-post cursor-pointer"
                                    ref={imgRef}
                                    loading="lazy"
                                    onLoad={(e) => {
                                        const img = e.currentTarget;
                                        const diff = img.naturalWidth - img.naturalHeight;
                                        if (Math.abs(diff) > 50 && img.naturalWidth > img.naturalHeight) setOrientation("horizontal");
                                    }}
                                />
                            </span>
                        </div>
                    </div>
                ) : images[0].type === 2 ? (
                    <div className="row-audio-post post-chat-audio">
                        <ChatAudioMessage
                            src={getImagePath(images[0].path) || ""}
                            avatarPath={audioAvatarPath}
                            avatarName={audioAvatarName}
                        />
                    </div>
                ) : (
                    <div className="row row-video-post" ref={containerRef} style={{ margin: 0, marginTop: 5 }}>
                        <div className="post-video-frame" style={{ aspectRatio: videoAspectRatio }}>
                            <ReactPlayer
                                className="post-video-player"
                                width="100%"
                                height="100%"
                                muted={mutedVideo}
                                loop
                                ref={videoRef}
                                url={getImagePath(images[0].path)}
                                controls
                                playing={isPlaying}
                                onReady={verificarOrientacaoVideo}
                            />
                        </div>
                    </div>
                )
            ) : (
                <>
                    <div className="row row-img-post">
                        <div className="col col-img-post scroll-none">
                            {images.map((image, index) => (
                                <React.Fragment key={index}>
                                    {image.type === 1 ? (
                                        <div className="img-post multiple-video-post">
                                            <ReactPlayer className="post-video-player" controls height="100%" width="100%" url={getImagePath(image.path)} />
                                        </div>
                                    ) : image.type === 0 ? (
                                        <img
                                            onClick={() => openImage === index ? setOpenImage(null) : setOpenImage(index)}
                                            alt="Imagem da publicação"
                                            onError={(e) => (e.currentTarget.src = "/img/image_not_found.png?v=1")}
                                            src={getImagePath(image.path)}
                                            className="multiple-img-post"
                                            loading="lazy"
                                        />
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                    {images[images?.length - 1].type == 2 ? (
                        <div className="col w-100 row-audio-post post-chat-audio" style={{ display: "flex", overflow: "auto" }}>
                            <ChatAudioMessage
                                src={getImagePath(images[images?.length - 1].path) || ""}
                                avatarPath={audioAvatarPath}
                                avatarName={audioAvatarName}
                            />
                        </div>
                    ) : null}
                </>
            )}

            {/* Modal com carrossel — passa todas as imagens e o índice clicado */}
            <ImageModal
                images={images}
                initialIndex={openImage ?? 0}
                open={openImage !== null}
                onClose={closeImageModal}
                username={post?.user?.user_name}
                timeAgo={"há 2 horas"}
                likes={post?.total_likes}
                post={post}
                comments={post?.total_comments}
                isLiked={post?.liked}
                onLike={() => {}}
                img_account={img_account}
                onComment={onComment}
                setPosts={setPosts}
                setPost={setPost}
                onShare={onShare}
                getImagePath={getImagePath}
            />
        </>
    );
};

export default ImgPost;
